import React from 'react';
import Icon from 'components/AppIcon';

const StatisticsSection = () => {
  const statisticsData = {
    title: "Nuestros Logros en Números",
    subtitle: "Our Achievements in Numbers",
    description: "Estos números reflejan nuestro compromiso constante con la excelencia y la satisfacción de nuestros clientes en Costa Rica.",
    stats: [
      {
        number: "500+",
        label: "Clientes Satisfechos",
        labelEn: "Satisfied Customers",
        description: "Familias y empresas que confían en nuestros servicios",
        icon: "Users",
        color: "primary"
      },
      {
        number: "8+",
        label: "Años de Experiencia",
        labelEn: "Years of Experience",
        description: "Construyendo confianza en el mercado costarricense",
        icon: "Calendar",
        color: "success"
      },
      {
        number: "15,000+",
        label: "Servicios Completados",
        labelEn: "Services Completed",
        description: "Limpiezas y mantenimientos realizados exitosamente",
        icon: "CheckCircle",
        color: "accent"
      },
      {
        number: "98%",
        label: "Tasa de Retención",
        labelEn: "Retention Rate",
        description: "Clientes que renuevan nuestros servicios anualmente",
        icon: "TrendingUp",
        color: "primary"
      },
      {
        number: "24h",
        label: "Tiempo de Respuesta",
        labelEn: "Response Time",
        description: "Atención a emergencias y consultas urgentes",
        icon: "Clock",
        color: "warning"
      },
      {
        number: "7",
        label: "Provincias Cubiertas",
        labelEn: "Provinces Covered",
        description: "Cobertura de servicio en todo Costa Rica",
        icon: "MapPin",
        color: "success"
      }
    ],
    achievements: [
      {
        title: "Empresa Líder en San José",
        description: "Reconocida como la empresa de limpieza de piscinas más confiable en el área metropolitana",
        year: "2023"
      },
      {
        title: "Certificación Ambiental",
        description: "Primera empresa del sector en obtener certificación de prácticas ambientales responsables",
        year: "2022"
      },
      {
        title: "Premio a la Excelencia",
        description: "Galardón otorgado por la Cámara de Comercio por servicio al cliente excepcional",
        year: "2021"
      }
    ]
  };

  const getColorClasses = (color) => {
    const colorMap = {
      primary: "text-primary bg-primary/10",
      success: "text-success bg-success/10",
      accent: "text-accent bg-accent/10",
      warning: "text-warning bg-warning/10"
    };
    return colorMap[color] || colorMap.primary;
  };

  return (
    <section className="mb-16">
      <div className="text-center mb-12">
        <h2 className="text-3xl lg:text-4xl font-heading font-bold text-text-primary mb-4">
          {statisticsData.title}
        </h2>
        <p className="text-lg text-text-secondary font-medium mb-6">
          {statisticsData.subtitle}
        </p>
        <p className="max-w-3xl mx-auto text-text-secondary">
          {statisticsData.description}
        </p>
      </div>

      {/* Statistics Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
        {statisticsData.stats.map((stat, index) => (
          <div key={index} className="bg-surface p-6 rounded-lg border border-border shadow-subtle text-center hover:shadow-medium nav-transition">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${getColorClasses(stat.color)}`}>
              <Icon name={stat.icon} size={32} />
            </div>
            <div className="text-3xl lg:text-4xl font-heading font-bold text-text-primary mb-2">
              {stat.number}
            </div>
            <h3 className="font-heading font-semibold text-text-primary mb-1">
              {stat.label}
            </h3>
            <p className="text-sm text-text-secondary font-medium mb-2">
              {stat.labelEn}
            </p>
            <p className="text-xs text-text-secondary">
              {stat.description}
            </p>
          </div>
        ))}
      </div>

      {/* Achievements Section */}
      <div className="bg-gradient-to-r from-primary/5 to-accent/5 rounded-lg p-8">
        <h3 className="text-2xl font-heading font-bold text-text-primary text-center mb-8">
          Reconocimientos y Logros
        </h3>
        
        <div className="grid md:grid-cols-3 gap-6">
          {statisticsData.achievements.map((achievement, index) => (
            <div key={index} className="bg-surface p-6 rounded-lg border border-border text-center">
              <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Award" size={24} className="text-accent" />
              </div>
              <div className="text-lg font-heading font-bold text-primary mb-2">
                {achievement.year}
              </div>
              <h4 className="font-heading font-semibold text-text-primary mb-2">
                {achievement.title}
              </h4>
              <p className="text-sm text-text-secondary">
                {achievement.description}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Service Coverage Map Info */}
      <div className="mt-12 grid lg:grid-cols-2 gap-8 items-center">
        <div>
          <h3 className="text-2xl font-heading font-bold text-text-primary mb-4">
            Cobertura de Servicio en Costa Rica
          </h3>
          <p className="text-text-secondary mb-6">
            Brindamos servicios profesionales de limpieza y mantenimiento de piscinas en las principales provincias de Costa Rica, con especial enfoque en el área metropolitana de San José.
          </p>
          
          <div className="space-y-3">
            {[
              "San José - Área Metropolitana",
              "Cartago - Valle Central",
              "Alajuela - Aeropuerto y alrededores",
              "Heredia - Zona Norte",
              "Puntarenas - Zona Costera",
              "Guanacaste - Playas del Pacífico",
              "Limón - Costa Caribeña"
            ].map((province, index) => (
              <div key={index} className="flex items-center space-x-3">
                <div className="w-6 h-6 bg-success/10 rounded-full flex items-center justify-center">
                  <Icon name="MapPin" size={14} className="text-success" />
                </div>
                <span className="text-text-secondary">{province}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-surface rounded-lg border border-border overflow-hidden">
          <iframe
            width="100%"
            height="300"
            loading="lazy"
            title="Cobertura de Servicio BAC Pool CR en Costa Rica"
            referrerPolicy="no-referrer-when-downgrade"
            src="https://www.google.com/maps?q=9.9281,-84.0907&z=8&output=embed"
            className="w-full h-full"
          ></iframe>
        </div>
      </div>
    </section>
  );
};

export default StatisticsSection;